import type { BaseAppointmentType } from '#components/Autocomplete/BaseAppointmentTypes/BaseAppointmentTypesAutocomplete.types.ts';
import type { ProviderFilter } from '#utils/provider-filter.ts';

import axios from 'axios';
import { z } from 'zod';

import { config } from '#config.ts';
import { mia } from '#services/http-clients.ts';
import { handleMiaUnauthorized, MiaUnauthorizedError } from '#services/mia-session.ts';
import { getAccessToken } from '#utils/cookies.ts';

/**
 * Optional multi-select narrowing shared by the three Coordinate feeds and the slot-offer action
 * (the Provider Filter and the Appointment Type Filter's two axes). The server rejects empty
 * arrays, so builders omit inactive dimensions. Explicit all-provider scope travels as the scalar
 * `all`; arrays travel as repeated query params (feeds) or JSON arrays (actions).
 */
export interface FeedFilters {
  providerIds?: ProviderFilter;
  appointmentTypeIds?: number[];
  baseAppointmentTypes?: BaseAppointmentType[];
}

const outreachExecutionStatusSchema = z.enum([
  'queued',
  'processing',
  'retrying',
  'accepted',
  'failed',
  'skipped',
  'delivery_unknown',
]);

export type OutreachExecutionStatus = z.infer<typeof outreachExecutionStatusSchema>;

export interface OutreachAttemptCounts {
  total: number;
  queued: number;
  processing: number;
  retrying: number;
  accepted: number;
  failed: number;
  skipped: number;
  deliveryUnknown: number;
}

/** Active or latest durable outreach state projected onto a workflow feed row. */
export interface OutreachProjection {
  runId: number;
  trigger: 'staff' | 'scheduled';
  targetId: number;
  status: OutreachExecutionStatus;
  version: number;
  attemptCounts: OutreachAttemptCounts;
  unrecordedContactCount: number;
  updatedAt: string;
}

/** The durable outreach run accepted by each bulk trigger endpoint. */
export interface CreateOutreachRunResponse {
  runId: number;
  status: 'queued';
  targetCount: number;
  projectedAttemptCount: number;
  statusUrl: string;
  targetsUrl: string;
  streamUrl: string;
}

export type OutreachWorkflow = 'confirmation' | 'rebooking' | 'slot_fill';

/**
 * One Outreach Run's own lifecycle — deliberately not the seven-value execution status a target or
 * attempt carries. A run is `running` while any target is unsettled and terminal once none are, so
 * this is the server's own answer to "is the batch finished", rather than one the client re-derives
 * by counting patches it may have missed.
 */
export type OutreachRunStatus = 'queued' | 'running' | 'completed' | 'failed';

/** Whether the run has stopped moving — nothing further will change. */
export function isTerminalRunStatus(status: OutreachRunStatus): boolean {
  return status === 'completed' || status === 'failed';
}

/** The staff-facing summary of one Outreach Run, read back by `runId` after the trigger's 202. */
export interface OutreachRunSummary {
  runId: number;
  orgId: number;
  trigger: 'staff' | 'scheduled';
  workflow: OutreachWorkflow;
  status: OutreachRunStatus;
  targetCount: number;
  attemptCounts: OutreachAttemptCounts;
  completedAt: string | null;
}

/** Reads one run's current state. The trigger's `statusUrl` addresses this same resource. */
export function getOutreachRun(params: {
  orgId: number;
  runId: number;
}): Promise<OutreachRunSummary> {
  return mia
    .get<OutreachRunSummary>(`/outreach/runs/${params.runId}`, {
      params: { orgId: params.orgId },
    })
    .then((response) => response.data);
}

const outreachTargetPatchSchema = z.object({
  entityId: z.number().int().positive(),
  runId: z.number().int().positive(),
  trigger: z.enum(['staff', 'scheduled']),
  targetId: z.number().int().positive(),
  status: outreachExecutionStatusSchema,
  attemptCounts: z.object({
    total: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    deliveryUnknown: z.number().int().nonnegative(),
  }),
  unrecordedContactCount: z.number().int().nonnegative().default(0),
  version: z.number().int().positive(),
  reason: z.string().min(1).nullable(),
  updatedAt: z.string().datetime(),
});

export type OutreachTargetPatch = z.infer<typeof outreachTargetPatchSchema>;

/** Whether starting the workflow again could duplicate a pending or possibly delivered message. */
export function canStartOutreach(outreach: OutreachProjection | null): boolean {
  return (
    outreach === null ||
    outreach.status === 'failed' ||
    outreach.status === 'skipped' ||
    outreach.status === 'delivery_unknown'
  );
}

const sseLineEndingPattern = /\r\n|[\r\n]/;
const sseFrameBoundaryPattern = /(?:\r\n|[\r\n]){2}/;

function parseOutreachTargetPatch(frame: string): OutreachTargetPatch | null {
  const payload = frame
    .split(sseLineEndingPattern)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).trimStart())
    .join('\n');

  if (!payload) {
    return null;
  }

  try {
    const parsed = outreachTargetPatchSchema.safeParse(JSON.parse(payload));
    if (!parsed.success) {
      console.warn('[mia] Discarded outreach stream frame with an unexpected shape.', {
        issues: parsed.error.issues.map((issue) => ({
          code: issue.code,
          path: issue.path.map(String).join('.'),
        })),
      });
      return null;
    }

    return parsed.data;
  } catch {
    console.warn('[mia] Discarded outreach stream frame containing invalid JSON.');
    return null;
  }
}

/** Opens one authenticated workflow stream and resolves if the connection closes. */
export async function streamOutreachTargetPatches(options: {
  workflow: OutreachWorkflow;
  orgId: number;
  signal: AbortSignal;
  onOpen: () => Promise<void>;
  onPatch: (patch: OutreachTargetPatch) => void;
}): Promise<void> {
  const accessToken = getAccessToken();
  if (!accessToken) {
    handleMiaUnauthorized();
    throw new MiaUnauthorizedError();
  }

  const streamUrl = new URL(`/outreach/streams/${options.workflow}`, config.MIA_API_BASE_URL);
  streamUrl.searchParams.set('orgId', String(options.orgId));
  const response = await fetch(streamUrl, {
    headers: { Accept: 'text/event-stream', 'X-Access-Token': accessToken },
    signal: options.signal,
  });

  if (response.status === 401) {
    handleMiaUnauthorized();
    throw new MiaUnauthorizedError();
  }

  if (!response.ok || !response.body) {
    throw new Error(`Could not open outreach stream (${response.status}).`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // Refresh after the transport opens but before consuming its buffered events. This recovers
    // the reconnect interval without allowing that snapshot to overwrite a newer patch; the
    // existing feed polling remains the backstop for the server's cursorless startup edge.
    await options.onOpen();

    while (!options.signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      let boundary = sseFrameBoundaryPattern.exec(buffer);
      while (boundary) {
        const frame = buffer.slice(0, boundary.index);
        buffer = buffer.slice(boundary.index + boundary[0].length);
        const patch = parseOutreachTargetPatch(frame);
        if (patch) {
          options.onPatch(patch);
        }
        boundary = sseFrameBoundaryPattern.exec(buffer);
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      // The fetch abort path has already canceled the body.
    }
    reader.releaseLock();
  }
}

interface ListRebookingCandidatesParams extends FeedFilters {
  orgId: number;
}

export type RebookingCandidateStatus =
  | 'needs_outreach'
  | 'contacted'
  | 'waitlisted'
  | 'staff_follow_up';

export type RebookingEventKind = 'no_show' | 'late_cancel' | 'canceled';

/**
 * Raw attendance history for the row's effective (household) patient. All fields share the
 * server-configured history window, so `isNewPatient` means no past appointments in that scope.
 */
export interface PatientRiskFactors {
  noShowCount: number;
  lateCancelCount: number;
  totalAppointmentsInWindow: number;
  isNewPatient: boolean;
}

export interface RebookingCandidate {
  appointmentId: number;
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientBirthDate: string | null;
  providerId: number;
  providerFirstName: string | null;
  providerLastName: string | null;
  appointmentTime: string;
  eventAt: string;
  eventKind: RebookingEventKind;
  patientPhone: string | null;
  patientEmail: string | null;
  preferredChannel: MiaMessageChannel | null;
  appointmentTypeId: number;
  /** Production data guarantees a matching type row despite the backend schema allowing null. */
  appointmentTypeName: string;
  riskFactors: PatientRiskFactors;
  status: RebookingCandidateStatus;
  outreach: OutreachProjection | null;
}

interface ListRebookingCandidatesResponse {
  candidates: RebookingCandidate[];
  /** The server-resolved sender every Rebooking Outreach path introduces. */
  messageAuthorName: string;
}

/**
 * Lists up to the 200 most recent qualifying canceled or missed appointments, each with its own
 * workflow status and outreach state. A patient may appear more than once.
 */
export function listRebookingCandidates(
  params: ListRebookingCandidatesParams
): Promise<ListRebookingCandidatesResponse> {
  return mia
    .get<ListRebookingCandidatesResponse>('/mia/rebooking/candidates', { params })
    .then((response) => response.data);
}

export interface AskAboutRebookingRequest {
  orgId: number;
  appointmentIds: number[];
}

/** Queues Mia's rebooking outreach after a canceled or missed appointment. */
export function askAboutRebooking(
  request: AskAboutRebookingRequest
): Promise<CreateOutreachRunResponse> {
  return mia
    .post<CreateOutreachRunResponse>('/mia/rebooking/ask', request)
    .then((response) => response.data);
}

export type MiaMessageChannel = 'email' | 'sms';

export type UnconfirmedRowStatus = 'not_contacted' | 'awaiting_confirmation' | 'confirmed';

interface GetConfirmationWorklistParams extends FeedFilters {
  orgId: number;
  /** How many days of upcoming appointments to include. Server default is 2, max is 14. */
  daysAhead?: number;
}

/**
 * Which deployed model scores an org's appointments. Per-org homogeneous and derived server-side
 * from no-show-cohort membership — a read-side property, never a settable knob. Mirrors
 * `riskKindSchema` in care-coordinator-agent's `libs/shared`, and keys all kind-aware briefing copy.
 */
export type RiskKind = 'no_show' | 'late_cancel';

/**
 * Mirrors `confirmationWorklistRowSchema` in care-coordinator-agent's `libs/shared`. Every field
 * is always present, `null` when absent. Datetime fields are ISO-8601 strings over the REST
 * mirror (they are `Date`s only over tRPC + superjson, which we don't use). `preferredChannel` is
 * the server-side (SMS-first) prediction of what a send would pick. Name fields have no length
 * floor — a provider with blank names comes through as an empty `providerName` string.
 */
interface UnconfirmedRowBase {
  appointmentId: number;
  patientId: number;
  providerId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientBirthDate: string | null;
  patientPhone: string | null;
  appointmentStartsAt: string;
  providerName: string;
  locationName: string | null;
  preferredChannel: MiaMessageChannel | null;
  appointmentTypeId: number;
  appointmentTypeName: string;
  riskFactors: PatientRiskFactors;
  /**
   * The deployed model's predicted probability for the org's risk kind. Only the field matching the
   * envelope's `riskKind` is ever populated; the other stays null. Both are null when the
   * appointment is unscored — BigQuery unconfigured, the prediction missing, or the server's read
   * failed and degraded — so a null is "unknown risk", never "low risk".
   */
  predictedNoShowProbability: number | null;
  predictedLateCancelProbability: number | null;
  outreach: OutreachProjection | null;
}

interface NotContactedUnconfirmedRow extends UnconfirmedRowBase {
  status: 'not_contacted';
  lastContactedAt: null;
  contactCount: 0;
  conversationId: null;
  confirmedAt: null;
}

interface AwaitingConfirmationUnconfirmedRow extends UnconfirmedRowBase {
  status: 'awaiting_confirmation';
  lastContactedAt: string;
  contactCount: number;
  conversationId: number;
  confirmedAt: null;
}

interface ConfirmedUnconfirmedRow extends UnconfirmedRowBase {
  status: 'confirmed';
  lastContactedAt: string;
  contactCount: number;
  conversationId: number;
  confirmedAt: string;
}

/**
 * A discriminated union the server's schema doesn't declare but its implementation guarantees:
 * the worklist only emits rows that are unconfirmed OR have outreach (the union predicate in
 * `reminder-repository.getConfirmationWorklist`), and `deriveConfirmationStatus` in
 * `reminder-service` maps outreach + confirmation onto `status` — together making the
 * outreach/confirmation fields non-null exactly as each variant states. If the server ever
 * relaxes that predicate, these variants — not just the flat field types — must be revisited.
 */
export type UnconfirmedRow =
  | NotContactedUnconfirmedRow
  | AwaitingConfirmationUnconfirmedRow
  | ConfirmedUnconfirmedRow;

export interface GetConfirmationWorklistResponse {
  rows: UnconfirmedRow[];
  riskKind: RiskKind;
  /**
   * How much notice Confirmation Outreach has to give, in whole minutes. Mirrors
   * `confirmationLeadTimeFloorMinutes` in care-coordinator-agent's `libs/shared`. On the wire so the
   * client holds no literal of its own, which would one day predict a send the server refuses.
   */
  confirmationLeadTimeFloorMinutes: number;
}

export function getConfirmationWorklist(
  params: GetConfirmationWorklistParams
): Promise<GetConfirmationWorklistResponse> {
  return mia
    .get<GetConfirmationWorklistResponse>('/reminders/confirmation-worklist', { params })
    .then((response) => response.data);
}

/** Where a feature's importance was sourced. `unavailable` → `features: []`. */
export type RiskModelScope = 'org' | 'global' | 'unavailable';

/**
 * One side of a feature's comparison. `outcomeRate` is the rate of the org's **risk event**
 * (no-showed / late-canceled), not attendance — never subtract it from 1 unless you deliberately want
 * the positive framing, which the wire permits but the product doesn't use.
 */
interface ComparisonGroup {
  outcomeRate: number;
  sampleSize: number;
}

/**
 * A descriptive comparison for a prediction feature — the "3× more likely" line. `favorableGroup` is
 * the feature's favorable side (e.g. patients with no prior no-shows), `referenceGroup` the rest.
 * Mirrors `riskFeatureComparisonSchema` in care-coordinator-agent's `libs/shared`.
 *
 * Exactly one feature per model carries one (the kind's own history input); it is null on every other
 * feature, and on that one whenever the server withholds it — either group under its sample floor, a
 * reference side that isn't actually riskier, or a favorable side with no outcomes at all. Every such
 * guard is server-side, so a null is simply "no line to draw" and the client adds no math of its own.
 */
export interface RiskFeatureComparison {
  favorableGroup: ComparisonGroup;
  referenceGroup: ComparisonGroup;
  /**
   * The relative risk `referenceGroup.outcomeRate / favorableGroup.outcomeRate` — a multiplier, not a
   * fractional increase. Guaranteed finite and strictly greater than 1.
   */
  riskRatio: number;
}

/**
 * One prediction feature. Mirrors `riskModelFeatureSchema` in care-coordinator-agent's
 * `libs/shared`: a model feature mapped to a human label + description, its `scoreShare` (the
 * "38% of the score" chip), and an optional descriptive `comparison`. No high/med/low banding —
 * raw `scoreShare` only.
 */
export interface RiskFeature {
  key: string;
  label: string;
  description: string;
  scoreShare: number;
  comparison: RiskFeatureComparison | null;
}

/**
 * The attendance-risk model explainer for an org. Mirrors `getRiskModelExplainerOutputSchema` in
 * care-coordinator-agent's `libs/shared`. `features` is ordered by `scoreShare` desc; the modal
 * renders only `features[0]` (the strongest predictor) — the full grid stays a deferred UI.
 *
 * `riskKind` names the outcome the comparison line reports ("more likely to no-show again" vs
 * "…cancel late again"), so it does reach the wording here — unlike the briefing and bucket copy,
 * which stay on the kind-agnostic "attendance risk" umbrella.
 */
export interface RiskModelExplainer {
  riskKind: RiskKind;
  scope: RiskModelScope;
  features: RiskFeature[];
}

interface GetRiskModelExplainerParams {
  orgId: number;
}

/**
 * Explains the org's attendance-risk model. `riskKind` is derived server-side from cohort
 * membership, so the caller only names the org.
 */
export function getRiskModelExplainer(
  params: GetRiskModelExplainerParams
): Promise<RiskModelExplainer> {
  return mia.get<RiskModelExplainer>('/risk-model', { params }).then((response) => response.data);
}

/**
 * Where an org's `riskThreshold` came from. `recommended` is computed from the org's own
 * predicted-risk distribution; `fallback` is one global constant standing in for orgs that have no
 * such value yet, which the client may replace with its own estimate. Mirrors
 * `thresholdSourceSchema` in care-coordinator-agent's `libs/shared`.
 */
export type ThresholdSource = 'saved' | 'recommended' | 'fallback';

/**
 * An org's resolved Mia config. Mirrors `orgConfigSchema` in care-coordinator-agent's `libs/shared`;
 * `riskThreshold` is the predicted-probability floor (0..1) at or above which the daily briefing
 * flags an appointment. Its audit fields are null unless that threshold is a saved override.
 * Datetimes are ISO-8601 strings over the REST mirror.
 */
export interface MiaOrgConfig {
  orgId: number;
  riskThreshold: number;
  thresholdSource: ThresholdSource;
  updatedByUserId: number | null;
  updatedAt: string | null;
}

interface GetReadinessConfigParams {
  orgId: number;
}

/** Reads the org's Mia config. Staff-wide — every staff caller sees a threshold-filtered briefing. */
export function getReadinessConfig(params: GetReadinessConfigParams): Promise<MiaOrgConfig> {
  return mia.get<MiaOrgConfig>('/config', { params }).then((response) => response.data);
}

export interface SetReadinessConfigRequest {
  orgId: number;
  riskThreshold: number;
}

/** Saves the org's Risk Threshold override. Practice-admin gated server-side. */
export function setReadinessConfig(request: SetReadinessConfigRequest): Promise<MiaOrgConfig> {
  return mia.post<MiaOrgConfig>('/config', request).then((response) => response.data);
}

export type AutomaticOutreachWorkflow = 'confirmation' | 'rebooking';

export type AutomaticOutreachInteractionMode = 'confirm_by_link' | 'call_or_join_waitlist';

/** One workflow's Automatic Outreach configuration. */
export interface AutomaticOutreachConfig {
  orgId: number;
  workflow: AutomaticOutreachWorkflow;
  enabled: boolean;
  enabledByUserId: number | null;
  enabledAt: string | null;
  updatedByUserId: number | null;
  updatedAt: string | null;
  interactionMode: AutomaticOutreachInteractionMode | null;
  effectiveInteractionMode: AutomaticOutreachInteractionMode;
  interactionModeSource: 'default' | 'saved';
  interactionModeUpdatedByUserId: number | null;
  interactionModeUpdatedAt: string | null;
  localHour: number;
}

export interface GetAutomaticOutreachConfigParams {
  orgId: number;
  workflow: AutomaticOutreachWorkflow;
}

/** Reads one workflow's Automatic Outreach configuration. */
export function getAutomaticOutreachConfig(
  params: GetAutomaticOutreachConfigParams
): Promise<AutomaticOutreachConfig> {
  return mia
    .get<AutomaticOutreachConfig>('/automatic-outreach/config', { params })
    .then((response) => response.data);
}

export interface SetAutomaticOutreachConfigRequest extends GetAutomaticOutreachConfigParams {
  enabled: boolean;
}

/** Enables or disables one Automatic Outreach workflow. */
export function setAutomaticOutreachConfig(
  request: SetAutomaticOutreachConfigRequest
): Promise<AutomaticOutreachConfig> {
  return mia
    .post<AutomaticOutreachConfig>('/automatic-outreach/config', request)
    .then((response) => response.data);
}

export interface SendRemindersRequest {
  orgId: number;
  appointmentIds: number[];
}

export function sendReminders(request: SendRemindersRequest): Promise<CreateOutreachRunResponse> {
  return mia
    .post<CreateOutreachRunResponse>('/reminders/send', request)
    .then((response) => response.data);
}

export interface PreviewMessageContentRequest {
  kind: 'confirmation' | 'rebooking';
  appointmentId: number;
  channel: MiaMessageChannel;
}

export interface PreviewMessageContentResponse {
  channel: MiaMessageChannel;
  body: string;
  /** Present for email, absent for SMS. */
  subject?: string;
  /** "mastra:vertex" when the LLM wrote the copy, "fallback" for the deterministic template. */
  provider: string;
  model?: string;
}

/** Generates the message Mia would send for an appointment without sending it. */
export function previewMessageContent(
  request: PreviewMessageContentRequest
): Promise<PreviewMessageContentResponse> {
  return mia
    .post<PreviewMessageContentResponse>('/mia/messaging/preview', request)
    .then((response) => response.data);
}

/** Mirrors `sendPreparedInputSchema` in care-coordinator-agent's `libs/shared`. */
export interface SendPreparedRequest {
  // `topic` picks which thread the verbatim copy lands on: the appointment's confirmation thread or
  // its rebooking thread. Both appointment topics carry the same `appointmentId`.
  link: { topic: 'appointment-confirmation' | 'rebooking'; appointmentId: number };
  channel: MiaMessageChannel;
  body: string;
  /** Present for email, omit for SMS. */
  subject?: string;
}

/** Mirrors `sendPreparedOutputSchema` in care-coordinator-agent's `libs/shared`. */
export type SendPreparedResponse =
  | {
      outcome: 'sent';
      conversationId: number;
      conversationMessageId: number;
      externalMessageId?: string;
    }
  | {
      outcome: 'skipped';
      reason:
        | 'invalid_address'
        | 'missing_channel_recipient'
        | 'rate_limited'
        | 'conversation_unavailable';
    }
  | { outcome: 'failed'; reason: 'dispatch_failed' };

/**
 * Dispatches verbatim, staff-vetted copy for one appointment — the exact body/subject/channel the
 * staff previewed. Deliberately bypasses suppression (reaches opted-out and hard-bounced patients),
 * but rebooking still enforces its per-appointment cooldown and protects human-owned/closed
 * threads. The suppression override is audited server-side as `suppressionOverridden`. Requires a
 * staff-plus role server-side.
 */
export function sendPrepared(request: SendPreparedRequest): Promise<SendPreparedResponse> {
  return mia
    .post<SendPreparedResponse>('/mia/messaging/send', request)
    .then((response) => response.data);
}

export interface ListOfferableSlotsParams extends FeedFilters {
  orgId: number;
}

type SlotFillStatus = 'open' | 'claimed' | 'filled' | 'expired';
type SlotOfferStatus = 'pending' | 'offered' | 'claimed' | 'declined' | 'lost' | 'expired';

interface SlotOfferState {
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  status: SlotOfferStatus;
  offeredAt: string | null;
}

interface SlotFillState {
  appointmentId: number;
  slotFillId: number;
  status: SlotFillStatus;
  offers: SlotOfferState[];
}

/**
 * One Offerable Slot: a slot-fill-eligible late-canceled appointment. `slotFillState` is null
 * until a blast has been triggered for it — the UI shows the offer button in that state, a state
 * chip once it's set.
 */
export interface OfferableSlot {
  appointmentId: number;
  providerId: number;
  providerFirstName: string | null;
  providerLastName: string | null;
  appointmentTypeName: string;
  slotStartsAt: string;
  slotEndsAt: string;
  canceledAt: string;
  candidates: OfferableSlotCandidate[];
  slotFillState: SlotFillState | null;
  outreach: OutreachProjection | null;
}

export interface OfferableSlotCandidate {
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientBirthDate: string | null;
  patientPhone: string | null;
  preferredChannel: MiaMessageChannel | null;
  waitlistedAt: string;
}

/** Enrollment projection from the coordinator; REST timestamps are UTC ISO strings. */
export interface WaitlistEntry {
  id: number;
  orgId: number;
  patientId: number;
  providerId: number;
  appointmentTypeId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientPhone: string | null;
  providerFirstName: string | null;
  providerLastName: string | null;
  appointmentTypeName: string | null;
  status: 'ACTIVE' | 'WITHDRAWN' | 'FULFILLED';
  notes: string | null;
  createdByUserId: number;
  createdAt: string;
  source: 'manual' | 'rebooking_outreach';
  nextAppointment: { id: number; startDate: string } | null;
  lastOfferedAt: string | null;
  /** API-owned eligibility, independent of historical Last offer display. */
  removalBlockedReason: 'not_active' | 'outstanding_offer' | null;
}

/** The count represents enrollments, including separate criteria for the same patient. */
interface ListWaitlistResponse {
  entries: WaitlistEntry[];
  totalCount: number;
}

/** Staff-authorized organization-wide read, independent of slot availability. */
export function listWaitlist(params: {
  orgId: number;
  status?: WaitlistEntry['status'];
  sortBy?: 'createdAt';
  sortDirection?: 'asc' | 'desc';
}): Promise<ListWaitlistResponse> {
  return mia
    .get<ListWaitlistResponse>('/mia/waitlist', { params })
    .then((response) => response.data);
}

/** Staff enrollment accepts criteria only; attribution and lifecycle belong to the API. */
interface StaffEnrollmentInput {
  orgId: number;
  patientId: number;
  providerId: number;
  appointmentTypeId: number;
}

/** Returns the enriched enrollment; duplicate criteria are an HTTP 409 admission error. */
export function enrollByStaff(request: StaffEnrollmentInput): Promise<WaitlistEntry> {
  return mia.post<WaitlistEntry>('/mia/waitlist', request).then((response) => response.data);
}

/** Soft-removes one enrollment; attribution and retry semantics belong to the API. */
export function removeWaitlistEnrollment(request: {
  orgId: number;
  waitlistId: number;
}): Promise<{ removed: true }> {
  return mia
    .delete<{ removed: true }>(`/mia/waitlist/${request.waitlistId}`, {
      params: { orgId: request.orgId },
    })
    .then((response) => response.data);
}

/** Shared explanation for preflight eligibility and a restriction discovered on submission. */
export const waitlistRemovalBlockedMessages = {
  not_active: 'This enrollment is no longer active.',
  outstanding_offer: 'This enrollment has an outstanding offer or is awaiting booking approval.',
} as const;

/** Preserve meaningful domain conflicts; unknown and access-gate failures use retry feedback. */
export function getRemoveWaitlistErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ message?: unknown } | null>(error)) {
    const message = error.response?.data?.message;
    if (message === 'waitlist_enrollment_has_outstanding_offer')
      return waitlistRemovalBlockedMessages.outstanding_offer;
    if (message === 'waitlist_enrollment_not_active')
      return waitlistRemovalBlockedMessages.not_active;
  }
  return 'Could not remove this enrollment. Please try again later.';
}

/** Explain duplicate enrollment; all other failures use generic retry feedback. */
export function getStaffEnrollmentErrorMessage(error: unknown): string {
  if (
    axios.isAxiosError<{ message?: unknown } | null>(error) &&
    error.response?.status === 409 &&
    error.response.data?.message === 'waitlist_enrollment_exists'
  ) {
    return 'This patient is already on the waitlist for this provider and visit type.';
  }
  return 'Could not add this patient. Please try again later.';
}

export interface ListOfferableSlotsResponse {
  enabled: boolean;
  offerBatchSize: number;
  slots: OfferableSlot[];
}

/** Mirrors `listOfferableSlotsOutputSchema` in care-coordinator-agent's `libs/shared`. */
export function listOfferableSlots(
  params: ListOfferableSlotsParams
): Promise<ListOfferableSlotsResponse> {
  return mia
    .get<ListOfferableSlotsResponse>('/mia/slot-fill/offerable', { params })
    .then((response) => response.data);
}

/**
 * The Appointment Type Filter's axes alone — what the slot-offer trigger accepts. Mirrors
 * `offerSlotToWaitlistInputSchema` in care-coordinator-agent's `libs/shared`: the offer contract
 * carries no provider axis (each slot fixes its provider), and the server strips one if sent.
 */
type AppointmentTypeFilter = Pick<FeedFilters, 'appointmentTypeIds' | 'baseAppointmentTypes'>;

/**
 * The active filter is part of the offer contract, not read-only decoration: offers launched
 * under a filter select only matching Slot-Fill Candidates, so a blast can never reach patients
 * the coordinator's filtered view was hiding. The server audits the batch as filter-constrained.
 */
export interface OfferSlotToWaitlistRequest extends AppointmentTypeFilter {
  orgId: number;
  appointmentIds: number[];
}

/**
 * Queues Slot-Fill Outreach for one or more freed slots as one durable run.
 */
export function offerSlotToWaitlist(
  request: OfferSlotToWaitlistRequest
): Promise<CreateOutreachRunResponse> {
  return mia
    .post<CreateOutreachRunResponse>('/mia/slot-fill/offer', request)
    .then((response) => response.data);
}

interface ApproveSlotFillRequest {
  slotFillId: number;
}

/**
 * Why a claimed slot couldn't be booked. Mirrors `bookingFailureReasonSchema` in
 * care-coordinator-agent's `libs/shared`.
 */
export type BookingFailureReason =
  | 'slot_gone'
  | 'type_misfit'
  | 'self_booking_disabled'
  | 'api_error';

/**
 * Mirrors `approveSlotFillOutputSchema` in care-coordinator-agent's `libs/shared`. A failed
 * booking changes nothing server-side — the slot stays claimed so staff can retry or release.
 */
export type ApproveSlotFillResponse =
  | { booked: true; appointmentId: number }
  | { booked: false; reason: BookingFailureReason };

/** Approves a claimed slot: Mia books the winner's waitlisted appointment type server-side. */
export function approveSlotFill(request: ApproveSlotFillRequest): Promise<ApproveSlotFillResponse> {
  return mia
    .post<ApproveSlotFillResponse>('/mia/slot-fill/approve', request)
    .then((response) => response.data);
}

interface ReleaseSlotFillClaimRequest {
  slotFillId: number;
}

/** Mirrors `releaseSlotFillClaimOutputSchema` in care-coordinator-agent's `libs/shared`. */
interface ReleaseSlotFillClaimResponse {
  released: boolean;
}

/**
 * The "Reject" action: releases the claim so the slot is back in play. Mia notifies the
 * released patient server-side.
 */
export function releaseSlotFillClaim(
  request: ReleaseSlotFillClaimRequest
): Promise<ReleaseSlotFillClaimResponse> {
  return mia
    .post<ReleaseSlotFillClaimResponse>('/mia/slot-fill/release', request)
    .then((response) => response.data);
}

// Rejection codes SlotFillService can throw when a race changes the slot's eligibility between
// the read and the click (org disabled/reconfigured mid-session, the cancellation ages out of the
// late-cancel window, the slot starts, or someone else's booking fills it first).
const slotFillErrorMessages: Record<string, string> = {
  slot_fill_disabled: 'Slot-Fill Outreach is turned off for this org.',
  slot_fill_config_invalid: "This org's Slot-Fill configuration is invalid.",
  not_late_cancel: 'This cancellation no longer qualifies as a Late Cancel.',
  slot_in_past: 'This slot has already started.',
  slot_refilled: 'This slot has since been filled — no further offers possible.',
};

// Extracts the typed rejection code Mia's REST mirror carries as the error body's `message`.
// data can be null/undefined on malformed upstream error bodies — guard it so the caller's
// fallback copy runs instead of the extraction itself throwing.
function getMiaErrorCode(error: unknown): string | null {
  if (axios.isAxiosError<{ message?: string } | null>(error) && error.response?.data?.message) {
    return error.response.data.message;
  }

  return null;
}

/** Maps an `offerSlotToWaitlist` rejection to human copy, falling back for unrecognized codes. */
export function getOfferSlotErrorMessage(error: unknown): string {
  const code = getMiaErrorCode(error);
  return (
    (code === null ? undefined : slotFillErrorMessages[code]) ??
    'Could not offer this slot. Please try again later.'
  );
}

// The Approve response's `booked: false` reasons, as staff-facing copy. Each leaves the row
// claimed and actionable — the toast explains why the booking didn't happen, not a state change.
// Exhaustively keyed so a new server-side reason fails the build here instead of silently
// falling through; the mirrored type is trust-not-validation, so unrecognized wire values are
// the mapper's fallback, not this table's concern.
const couldntBookReasonMessages: Record<BookingFailureReason, string> = {
  slot_gone: 'This slot is no longer open — it may have been rebooked.',
  type_misfit: "The patient's waitlisted appointment type doesn't fit this slot.",
  self_booking_disabled:
    "This organization has patient self-booking disabled, so Coordinate couldn't book it.",
  api_error: 'Mend could not complete the booking. Please try again.',
};

/** Maps a `booked: false` reason to human copy, falling back for unrecognized wire values. */
export function getCouldntBookMessage(reason: BookingFailureReason): string {
  return (
    (couldntBookReasonMessages as Record<string, string>)[reason] ??
    'Could not book this slot. Please try again.'
  );
}

// Rejection codes for the claim actions (approve/release): the claim changed under the staff
// click, or the record belongs to another org.
const claimActionErrorMessages: Record<string, string> = {
  slot_not_claimed: 'This slot was just updated — refreshing the list.',
  org_mismatch: "You don't have access to act on this slot.",
};

/** Maps an approve/release rejection to human copy, falling back for unrecognized codes. */
export function getClaimActionErrorMessage(error: unknown): string {
  const code = getMiaErrorCode(error);
  return (
    (code === null ? undefined : claimActionErrorMessages[code]) ??
    'Could not complete this action. Please try again later.'
  );
}
